import {
  RouteComponentProps,
  StaticContext,
  useParams,
  withRouter,
} from 'react-router';
import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
  BookingParams,
  BoxCount,
  BoxData,
  Discount,
  DiscountResponse,
  MakeOrderRequest,
  MakeOrderResponse,
  MakeSuitcaseOrderRequest,
  Product,
  Dimensions,
  CheckoutItem,
  CheckoutProduct,
  Order,
  MakePaymentRequest,
} from '../models/APIModels';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
  PaymentRequestButtonElement,
} from '@stripe/react-stripe-js';
import {
  loadStripe,
  PaymentIntent,
  SetupIntent,
  StripeError,
  ConfirmCardPaymentData,
  ConfirmCardSetupOptions,
  StripeCardElement,
  StripeCardNumberElement,
} from '@stripe/stripe-js';
import SubmitButton from '../components/SubmitButton';
import { Formik } from 'formik';
import Label from '../components/Label';
import AddressLookup, {
  Address,
  simplifyAddress,
  validateAddress,
} from '../components/AddressLookup';
import Title from '../components/Title';
import {
  boxToMoney,
  monthlyCostToMoney,
  payNow,
  toMoney,
  orderCostToMoney,
  suitcaseOrderToMoney,
} from '../utilities/MoneyConversion';
import { axiosKK } from '../services/networkRequest';
import NavigationStatus from '../components/NavigationStatus';
import useGtmEventTracking from '../components/useGtmEventTracking';
import errorMessage from '../services/errorMessage';
import { ThreeDSModal } from '../components/ThreeDSModal';
import { PaymentStatus } from '../models/APIModels';
import {
  COLLECTION_FEE,
  COLLECTION_SURCHARGE,
  SHIPPING_FEE,
  REDELIVER_FEE,
  REDELIVER_SURCHARGE,
  FURNITURE_DISASSEMBLY_FEE,
} from '../constants';
import UserContext from '../contexts/UserContext';
import { displayLoginName } from '../components/user/DisplayLoginName';
import { isOutOfArea, isDifferentArea } from '../utilities/Postcode';
import { adminCheck } from '../utilities/Role';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';
import { mapCountryToCountryCode } from '../utilities/Country';
import { useFlow } from '../utilities/useFlow';
import PackingRequirementsModal from '../components/PackingRequirementsModal';

// Removals Order Request Interface
interface RemovalOrderRequest {
  tierId: string;
  tierName: string;
  tierPrice: number;
  propertyQuestions: {
    floor: string;
    hasLift: boolean | null;
    hasFurniture: boolean;
    hasDisassembly: boolean | null;
    hasParking: boolean;
  };
  collectionAddress: string;
  collectionPostcode: string;
  collectionCity: string;
  collectionCountry: string;
  deliveryAddress: string;
  deliveryPostcode: string;
  deliveryCity: string;
  deliveryCountry: string;
  moveDate: number;
  packingMaterials?: {
    id: string;
    name: string;
    price: number;
    items: string[];
    rrp: number;
  };
  storage?: {
    id: string;
    name: string;
    size: string;
    price: number;
  };
  paymentMethodId: string;
  discountCode?: string;
  note?: string;
}

interface PaymentProps
  extends RouteComponentProps<undefined, StaticContext, PaymentState> {}

interface PaymentState {
  boxes: BoxData[];
  collectionAddress: Address;
  collectionDate: Date;
  deliveryAddress?: Address;
  dontKnowAddress: boolean;
  deliveryDate?: Date;
  dontKnowDate: boolean;
  storageRequired: boolean;
  suitcases?: Dimensions[];
  suitcaseData?: Product;
  eecProducts?: object[];
  items?: object[];
  orderId?: number;
  discount?: Discount;
  isFirstTimeTriggered?: boolean;
  existingOrder?: Order;
  fromConfirmation?: boolean;
  // Removals specific fields
  removalService?: boolean;
  selectedTier?: any;
  questions?: any;
  moveDate?: Date;
  selectedPacking?: any;
  selectedStorage?: any;
  wantsPacking?: boolean;
  wantsStorage?: boolean;
}

export interface PaymentFormErrors {
  address?: string;
  name?: string;
  cardNumberValidation?: string;
  cvcValidation?: string;
  expiryDateValidation?: string;
  agreeToTerms?: string;
  agreeToPackingRules?: string;
}

export interface PaymentFormInitialValues {
  name: string;
  cardNumberValidation: boolean;
  cvcValidation: boolean;
  expiryDateValidation: boolean;
  address: string;
  agreeToTerms: boolean;
  agreeToPackingRules: boolean;
}

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY!!);

const cardInputStyle = {
  style: {
    base: {
      fontSize: '14px',
      color: '#424770',
      fontWeight: '500',
      fontFamily: 'Montserrat, sans-serif',
      '::placeholder': {
        color: '#9e9e9e',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

const cardNumberOptions = {
  ...cardInputStyle,
  placeholder: '1234 5678 9012 3456',
};

const cardCvcOptions = {
  ...cardInputStyle,
  placeholder: 'CVC',
};

const cardExpiryOptions = {
  ...cardInputStyle,
  placeholder: 'MM / YY',
};

function PaymentComponent({ history }: PaymentProps): JSX.Element {
  const { state: parsedState } = history.location;
  const [state, setState] = useState<PaymentState | undefined>(parsedState);
  const isRemovalsFlow = history.location.pathname.includes('/removals') || 
                        (parsedState as any)?.removalService;
  const { user } = useContext(UserContext);
  const flow = useFlow();
  const [userLoggedIn, setUserLoggedIn] = useState(user && !adminCheck(user));

  useEffect(() => {
    if (state) {
      localStorage.setItem('payment-state', JSON.stringify(state));
    } else {
      const paymentState = localStorage.getItem('payment-state')!;
      const newState = JSON.parse(paymentState) as PaymentState;
      setState(newState);
    }
  }, [state]);

  useEffect(() => {
    setUserLoggedIn(user && !adminCheck(user));
  }, [user]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <section className="login-status-bar">
        <div className="status-bar-content">
          <p className="grey"> {displayLoginName(user)} </p>
        </div>
      </section>
      <NavigationStatus 
        step={isRemovalsFlow ? 5 : 3} 
        locationState={state}
      />
      <Title>Payment</Title>
      <p className="grey">
        {flow === 'Keepr' ? 'Keepr' : 'Kit Keeper'} are experts at storing and
        shipping boxes all across the UK.
      </p>

      {userLoggedIn ? (
        state && (
          <PaymentDefinedStateComponent
            paymentState={state}
            push={history.push}
            pathname={history.location.pathname}
          />
        )
      ) : (
        <div className="login">
          <div>
            <h2>Login to continue...</h2>
            <span className="grey">Don't have an account?</span>

            <div className="button-group">
              <SubmitButton
                onClick={(event) => {
                  event.preventDefault();
                  history.push(`/register`, {
                    ...state,
                    fromConfirmation: true,
                  } as PaymentState);
                }}
              >
                Register now
              </SubmitButton>

              <SubmitButton
                type="button"
                onClick={(event) => {
                  event.preventDefault();
                  history.push(`/login`, { ...state, fromConfirmation: true } as PaymentState);
                }}
              >
                <div>
                  <img
                    alt=""
                    src="/img/right-arrow-circular-button-outline.png"
                  />
                  Login
                </div>
              </SubmitButton>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

interface ApplePayButtonProps {
  paymentState: PaymentState;
  appliedDiscount?: Discount;
  onPaymentSuccess: (paymentMethod: any) => void;
}

function ApplePayButton({
  paymentState,
  appliedDiscount,
  onPaymentSuccess,
}: ApplePayButtonProps) {
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState<any>(null);

  useEffect(() => {
    if (!stripe) return;

    const {
      boxes,
      suitcases,
      suitcaseData,
      storageRequired,
      collectionAddress,
    } = paymentState;

    const needsEmptyBoxDelivery = boxes.some(
      (box) => box.name !== 'Own box' && box.count > 0,
    );

    let totalAmount = 0;

    if (suitcases && suitcaseData) {
      totalAmount = suitcases.length * suitcaseData.shippingCost;
    } else if (storageRequired) {
      const boxesTotal = boxes.reduce(
        (total, box) => total + box.count * box.purchaseCost,
        0,
      );
      totalAmount = boxesTotal + COLLECTION_FEE;

      if (needsEmptyBoxDelivery) {
        const deliveryFee = parseInt(process.env.REACT_APP_DELIVERY_FEE || '0');
        totalAmount += deliveryFee;
      }

      if (
        collectionAddress.postCode &&
        isOutOfArea(collectionAddress.postCode)
      ) {
        totalAmount += COLLECTION_SURCHARGE;
      }
    } else {
      const boxesTotal = boxes.reduce(
        (total, box) => total + box.count * box.purchaseCost,
        0,
      );

      // Calculate shipping cost
      const totalBoxes = boxes
        .filter((it) => it.chargeShipping)
        .map((it) => it.count);
      const totalBoxesCount = totalBoxes?.reduce((a, b) => a + b, 0);
      const ownBoxesCount = boxes
        .filter((it) => it.chargeShipping && it.name === 'Own box')
        .map((it) => it.count)
        .reduce((a, b) => a + b, 0);
      const otherBoxesCount = totalBoxesCount - ownBoxesCount;
      const deliveryCost =
        SHIPPING_FEE * otherBoxesCount + (SHIPPING_FEE + 500) * ownBoxesCount;

      totalAmount = boxesTotal + deliveryCost;

      if (needsEmptyBoxDelivery) {
        const deliveryFee = parseInt(process.env.REACT_APP_DELIVERY_FEE || '0');
        totalAmount += deliveryFee;
      }
    }

    // Store original total before discount for display items
    const originalTotalAmount = totalAmount;

    // Apply discount if available
    if (appliedDiscount?.upFront) {
      totalAmount =
        totalAmount * (1 - appliedDiscount.discountPercentage / 100);
    }

    // Create display items for Apple Pay sheet
    const displayItems = [];

    // Add box items
    boxes.forEach((box) => {
      if (box.count > 0) {
        displayItems.push({
          label: `${box.name} × ${box.count}`,
          amount: box.count * box.purchaseCost,
        });
      }
    });

    // Add fees
    if (storageRequired) {
      displayItems.push({
        label: 'Collection fee',
        amount: COLLECTION_FEE,
      });

      if (
        collectionAddress.postCode &&
        isOutOfArea(collectionAddress.postCode)
      ) {
        displayItems.push({
          label: 'Collection surcharge',
          amount: COLLECTION_SURCHARGE,
        });
      }
    }

    if (needsEmptyBoxDelivery) {
      const deliveryFee = parseInt(process.env.REACT_APP_DELIVERY_FEE || '0');
      displayItems.push({
        label: 'Delivery of empty boxes',
        amount: deliveryFee,
      });
    }

    // Add discount line
    if (appliedDiscount?.upFront) {
      const discountAmount = (originalTotalAmount * appliedDiscount.discountPercentage) / 100;
      
      // Ensure discountAmount is valid and finite
      if (isFinite(discountAmount)) {
        displayItems.push({
          label: `Discount (${appliedDiscount.discountPercentage}%)`,
          amount: -discountAmount,
        });
      }
    }

    // Validate all displayItems amounts are finite numbers
    const validDisplayItems = displayItems.filter((item) => 
      isFinite(item.amount) && !isNaN(item.amount)
    );

    // Ensure totalAmount is valid
    const finalAmount = isFinite(totalAmount) && !isNaN(totalAmount) ? Math.round(totalAmount) : 0;

    const pr = stripe.paymentRequest({
      country: 'GB',
      currency: 'gbp',
      total: {
        label: 'Kit Keeper',
        amount: finalAmount, // Stripe expects amounts in pence
      },
      displayItems: validDisplayItems,
      requestPayerName: true,
      requestPayerEmail: true,
    });

    // Check if Apple Pay is available
    pr.canMakePayment().then((result) => {
      if (result) {
        setPaymentRequest(pr);
      }
    });

    pr.on('paymentmethod', async (event) => {
      // Handle the payment method
      onPaymentSuccess(event.paymentMethod);
      event.complete('success');
    });
  }, [stripe, paymentState, appliedDiscount, onPaymentSuccess]);

  if (!paymentRequest) {
    return null;
  }

  return (
    <div className="apple-pay-container">
      <div className="payment-divider">
        <span>or</span>
      </div>
      <PaymentRequestButtonElement
        options={{ paymentRequest }}
        className="apple-pay-button"
      />
    </div>
  );
}

interface PaymentDefinedStateComponentProps {
  push: (path: string, state?: PaymentState) => void;
  paymentState: PaymentState;
  pathname: string;
}

function PaymentDefinedStateComponent({
  paymentState: state,
  push,
  pathname,
}: PaymentDefinedStateComponentProps) {
  const {
    boxes,
    collectionAddress,
    deliveryAddress,
    suitcases,
    storageRequired,
    suitcaseData,
  } = state;

  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();
  const [billingAddress, setBillingAddress] = useState<Address>({});
  const [orderId, setOrderId] = useState<number>();
  const [appliedDiscount, setAppliedDiscount] = useState<Discount>();
  const { route: routeParam } = useParams<BookingParams>();
  const isRemovalsFlow = pathname.includes('/removals') || state.removalService;
  const route = isRemovalsFlow ? 'removals' : routeParam;
  const [showPayNowDetails, setShowPayNowDetails] = useState(false);
  const [showPayMonthlyDetails, setShowPayMonthlyDetails] = useState(false);
  const [showFinalPaymentDetails, setShowFinalPaymentDetails] = useState(false);
  const [showPackingModal, setShowPackingModal] = useState(false);

  const totalBoxes = boxes
    .filter((it) => it.chargeShipping)
    .map((it) => it.count);
  const totalBoxesCount = totalBoxes?.reduce((a, b) => a + b, 0);

  const ownBoxesCount = boxes
    .filter((it) => it.chargeShipping && it.name === 'Own box')
    .map((it) => it.count)
    .reduce((a, b) => a + b, 0);
  const otherBoxesCount = totalBoxesCount - ownBoxesCount;

  const shippingRate = SHIPPING_FEE;
  const deliveryCost =
    shippingRate * otherBoxesCount + (shippingRate + 500) * ownBoxesCount;
  const needsEmptyBoxDelivery = boxes.some(
    (box) => box.name !== 'Own box' && box.count > 0,
  );

  const [billingEventSentToGTM, setBillingEventSentToGTM] = useState(false);
  const [paymentEventSentToGTM, setPaymentEventSentToGTM] = useState(false);

  const [selectedBillingOption, setSelectedBillingOption] = useState<
    string | null
  >('collection');
  const [showBillingAddressLookup, setShowBillingAddressLookup] =
    useState(false);

  // 3D Security
  const frontendUrl = process.env.REACT_APP_FRONTEND_URL;
  const [threeDSRedirectUrl, setThreeDSRedirectUrl] = useState<string>('');
  const [intentClientSecret, setIntentClientSecret] = useState<
    string | undefined
  >(undefined);
  const [paymentError, setPaymentError] = useState<string | undefined>(
    undefined,
  );
  const [paymentMethodId, setPaymentMethodId] = useState<string | undefined>(
    undefined,
  );
  const [cardNumber, setCardNumber] = useState<
    StripeCardElement | StripeCardNumberElement | null
  >(null);
  const [subscriptionCreated, setSubscriptionCreated] =
    useState<boolean>(false);

  let orderType: string;
  if (suitcases) {
    orderType = 'Suitcase';
  } else {
    if (storageRequired) {
      orderType = 'Box Storage';
    } else {
      orderType = 'Box Shipping';
    }
  }

  const numberOfBoxesWithReturnFee = boxes
    .filter((box) => box.returnFee && box.count > 0)
    .reduce((total, box) => total + box.count, 0);

  const returnFeeTotal = numberOfBoxesWithReturnFee * REDELIVER_FEE;

  const differentAreaSurcharge =
    storageRequired &&
    collectionAddress.postCode &&
    deliveryAddress?.postCode &&
    isDifferentArea(collectionAddress.postCode, deliveryAddress.postCode)
      ? numberOfBoxesWithReturnFee * REDELIVER_SURCHARGE
      : 0;

  const finalPaymentTotal = returnFeeTotal + differentAreaSurcharge;

  const eecCheckoutProducts: CheckoutProduct[] = [];
  const beginCheckoutItems: CheckoutItem[] = [];

  if (suitcases && suitcaseData) {
    for (let i = 0; i < suitcases.length; i++) {
      eecCheckoutProducts.push({
        name: suitcaseData.name,
        id: suitcaseData.id,
        price: (suitcaseData.purchaseCost / 100).toFixed(2),
        brand: 'Kit Keeper',
        category: orderType,
        quantity: 1,
        affiliation: 'Online Store',
      });

      let item: CheckoutItem = {
        item_id: suitcaseData.id.toString(),
        item_name: suitcaseData.name,
        affiliation: 'Online Store',
        currency: 'GBP',
        index: i,
        item_brand: 'Kit Keeper',
        item_category: orderType,
        price: (suitcaseData.purchaseCost / 100).toFixed(2),
        quantity: '1',
      };
      if (appliedDiscount) {
        item.coupon = appliedDiscount.discountCode;
        item.discount = appliedDiscount.discountPercentage + '%';
      }
      beginCheckoutItems.push(item);
    }
  } else {
    let storageCost = 0;

    boxes.forEach(function (box, i) {
      if (orderType === 'Storage') {
        storageCost += box.storageCost;
      }

      eecCheckoutProducts.push({
        name: box.name,
        id: box.id,
        price: (box.purchaseCost / 100).toFixed(2),
        brand: 'Kit Keeper',
        category: orderType,
        quantity: 1,
        affiliation: 'Online Store',
      });

      let item: CheckoutItem = {
        item_id: box.id.toString(),
        item_name: box.name,
        affiliation: 'Online Store',
        currency: 'GBP',
        index: i,
        item_brand: 'Kit Keeper',
        item_category: orderType,
        price: (box.purchaseCost / 100).toFixed(2),
        quantity: '1',
      };
      if (appliedDiscount) {
        item.coupon = appliedDiscount.discountCode;
        item.discount = appliedDiscount.discountPercentage + '%';
      }
      beginCheckoutItems.push(item);
    });

    if (storageCost > 0) {
      eecCheckoutProducts.push({
        name: 'Box Storage',
        price: (storageCost / 100).toFixed(2),
        brand: 'Kit Keeper',
        category: 'Storage',
        quantity: 1,
        affiliation: 'Online Store',
      });

      beginCheckoutItems.push({
        item_name: 'Box Storage',
        price: (storageCost / 100).toFixed(2),
        item_brand: 'Kit Keeper',
        item_category: 'Storage',
        quantity: '1',
        affiliation: 'Online Store',
        currency: 'GBP',
        index: 0,
      });
    }
  }

  const gtmEventTracking = useGtmEventTracking();

  useEffect(() => {
    if (state?.isFirstTimeTriggered) {
      gtmEventTracking({
        event: 'begin_checkout',
        ecommerce: {
          items: beginCheckoutItems,
        },
      });

      gtmEventTracking({
        event: 'eec_checkout',
        ecommerce: {
          checkout: {
            actionField: {
              step: 1,
            },
            products: eecCheckoutProducts,
          },
        },
      });

      gtmEventTracking({
        event: 'booking_step_payment',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gtmEventTracking, state?.isFirstTimeTriggered]);

  useEffect(() => {
    setBillingAddress(collectionAddress);
  }, [collectionAddress]);

  useEffect(() => {
    if (appliedDiscount) {
      if (appliedDiscount.upFront) {
        setShowPayNowDetails(true);
      }
      if (appliedDiscount.recurring && storageRequired) {
        setShowPayMonthlyDetails(true);
      }
    }
  }, [appliedDiscount, storageRequired]);

  const eecBillingObject = {
    event: 'eec_checkout',
    ecommerce: {
      checkout: {
        actionField: {
          step: 2,
        },
      },
    },
  };

  const billingInfoObject = {
    event: 'add_billing_info',
    ecommerce: {
      items: beginCheckoutItems,
    },
  };

  const eecPaymentObject = {
    event: 'eec_checkout',
    ecommerce: {
      checkout: {
        actionField: {
          step: 3,
        },
      },
    },
  };

  const paymentInfoObject = {
    event: 'add_payment_info',
    ecommerce: {
      currency: 'GBP',
      value: Number.parseFloat(
        (suitcases
          ? suitcaseOrderToMoney(
              suitcases.length,
              suitcaseData?.shippingCost,
              appliedDiscount,
            )
          : storageRequired
          ? payNow(
              boxes,
              appliedDiscount,
              needsEmptyBoxDelivery,
              collectionAddress,
            )
          : orderCostToMoney(boxes, needsEmptyBoxDelivery, appliedDiscount)
        ).substring(1),
      ),
      coupon: appliedDiscount ? appliedDiscount.discountCode : null,
      payment_type: 'Credit Card',
      items: beginCheckoutItems,
    },
  };

  const eventTrackingCallback = useCallback(
    (data: any) => {
      gtmEventTracking(data);
    },
    [gtmEventTracking],
  );

  const initialValues: PaymentFormInitialValues = {
    name: '',
    cardNumberValidation: false,
    cvcValidation: false,
    expiryDateValidation: false,
    address: '',
    agreeToTerms: false,
    agreeToPackingRules: false,
  };

  const handleIntent = useCallback(
    (
      intent: PaymentIntent | SetupIntent,
      orderId: number | undefined,
      finalState: PaymentState,
    ) => {
      const action = intent.next_action;
      if (
        intent.status === 'requires_action' &&
        action?.type === 'redirect_to_url'
      ) {
        //  Modal that contains an iframe
        const url = action?.redirect_to_url?.url;

        if (url) {
          setThreeDSRedirectUrl(url);

          push(`/${route}/payment/3ds-secure`);
        }
      } else if (intent.status === 'succeeded') {
        push(`/${route}/success`, { ...finalState, orderId });
      }
    },
    [push, route, setThreeDSRedirectUrl],
  );

  const handlePaymentStatus = useCallback(
    (
      data: MakeOrderResponse,
      finalState: PaymentState,
      cardNumber: StripeCardElement | StripeCardNumberElement,
      paymentMethodId: string,
    ) => {
      switch (data.paymentStatus) {
        case PaymentStatus.noneNeeded:
        case PaymentStatus.succeeded:
        case PaymentStatus.trialing:
          if (storageRequired && !subscriptionCreated && data.order.id) {
            const paymentRequest: MakePaymentRequest = {
              orderId: data.order.id,
              paymentMethodId: paymentMethodId,
            };

            axiosKK
              .post<MakeOrderResponse>(
                '/order/createSubscription',
                paymentRequest,
              )
              .then((response) => {
                if (
                  response.data.paymentStatus !== PaymentStatus.succeeded &&
                  response.data.setupIntentSecret &&
                  stripe !== null &&
                  cardNumber
                ) {
                  setSubscriptionCreated(true);
                  handlePaymentStatus(
                    response.data,
                    { ...state, orderId },
                    cardNumber,
                    paymentMethodId,
                  );
                }
              })
              .catch((error) => {
                setPaymentError(error.message);
              })
              .finally(() => {
                push(`/${route}/success`, { ...state, orderId });
              });
          }
          push(`/${route}/success`, { ...finalState, orderId: data.order.id });
          break;

        case PaymentStatus.requiresAction:
          const confirmData: ConfirmCardPaymentData = {
            payment_method: {
              card: cardNumber,
            },
            return_url: `${frontendUrl}/${route}/back-to-payment`,
          };

          const confirmOptions: ConfirmCardSetupOptions = {
            handleActions: false,
          };

          if (stripe) {
            if (data.paymentIntentSecret) {
              stripe
                .confirmCardPayment(
                  data.paymentIntentSecret,
                  confirmData,
                  confirmOptions,
                )
                .then(({ paymentIntent }) => {
                  if (paymentIntent) {
                    handleIntent(paymentIntent, data.order.id, finalState);
                  }
                });
            } else if (data.setupIntentSecret) {
              stripe
                .confirmCardSetup(
                  data.setupIntentSecret,
                  confirmData,
                  confirmOptions,
                )
                .then(({ setupIntent }) => {
                  if (setupIntent) {
                    handleIntent(setupIntent, data.order.id, finalState);
                  }
                });
            }
          }

          break;

        case PaymentStatus.requiresPaymentMethod:
          setPaymentError('Please try another payment method');
          break;
      }
    },
    [
      storageRequired,
      subscriptionCreated,
      stripe,
      push,
      route,
      state,
      frontendUrl,
      handleIntent,
      setSubscriptionCreated,
      setPaymentError,
      orderId,
    ],
  );

  const calculateDiscountAmount = () => {
    if (!appliedDiscount) return '';

    let baseAmount = 0;

    if (suitcases && suitcaseData) {
      baseAmount = suitcases.length * suitcaseData.shippingCost;
    } else if (storageRequired) {
      const boxesTotal = boxes.reduce(
        (total, box) => total + box.count * box.purchaseCost,
        0,
      );
      baseAmount = boxesTotal + COLLECTION_FEE;
      if (
        collectionAddress.postCode &&
        isOutOfArea(collectionAddress.postCode)
      ) {
        baseAmount += COLLECTION_SURCHARGE;
      }
    } else {
      const boxesTotal = boxes.reduce(
        (total, box) => total + box.count * box.purchaseCost,
        0,
      );
      baseAmount = boxesTotal + deliveryCost;
    }

    const discountAmount =
      (baseAmount * appliedDiscount.discountPercentage) / 100;
    return toMoney(discountAmount);
  };

  const calculateMonthlyDiscountAmount = () => {
    if (!appliedDiscount || !appliedDiscount.recurring) return '';

    const monthlyTotal = boxes.reduce(
      (total, box) => total + box.storageCost * box.count,
      0,
    );
    const discountAmount =
      (monthlyTotal * appliedDiscount.discountPercentage) / 100;
    return toMoney(discountAmount);
  };

  const handleRetrieveIntent = useCallback(
    (
      intent: PaymentIntent | SetupIntent | undefined,
      error: StripeError | undefined,
    ) => {
      setSubscriptionCreated(true);

      if (error) {
        setPaymentError(error.message);
      } else {
        if (intent?.status === PaymentStatus.succeeded) {
          if (storageRequired && !subscriptionCreated) {
            if (orderId && paymentMethodId) {
              const paymentRequest: MakePaymentRequest = {
                orderId: orderId,
                paymentMethodId: paymentMethodId,
              };

              axiosKK
                .post<MakeOrderResponse>(
                  '/order/createSubscription',
                  paymentRequest,
                )
                .then((response) => {
                  if (
                    response.data.paymentStatus !== PaymentStatus.succeeded &&
                    response.data.setupIntentSecret &&
                    stripe !== null &&
                    cardNumber
                  ) {
                    setSubscriptionCreated(true);
                    handlePaymentStatus(
                      response.data,
                      { ...state, orderId },
                      cardNumber,
                      paymentMethodId,
                    );
                  }
                })
                .catch((error) => {
                  setPaymentError(error.message);
                });
            } else {
              setPaymentError('Insufficient card or order details');
            }
          } else {
            push(`/${route}/success`, { ...state, orderId });
          }
        } else if (intent?.status === PaymentStatus.requiresPaymentMethod) {
          setPaymentError('Please try another payment method');
        }
      }
    },
    [
      orderId,
      subscriptionCreated,
      paymentMethodId,
      storageRequired,
      stripe,
      cardNumber,
      handlePaymentStatus,
      push,
      route,
      state,
      setSubscriptionCreated,
      setPaymentError,
    ],
  );

  useEffect(() => {
    let listener = (event: MessageEvent) => {
      if (
        event.origin === frontendUrl &&
        event.data === '3DS-authentication-complete'
      ) {
        intentClientSecret &&
          stripe!
            .retrievePaymentIntent(intentClientSecret)
            .then(({ paymentIntent, error }) => {
              handleRetrieveIntent(paymentIntent, error);
            });
      }
    };
    window.addEventListener('message', listener);

    return () => {
      window.removeEventListener('message', listener);
    };
  });

  const deliveryFeeString = process.env.REACT_APP_DELIVERY_FEE;
  if (!deliveryFeeString) {
    throw new Error('No delivery fee set');
  }
  const deliveryFee = parseInt(deliveryFeeString);

  const flow = useFlow();

  const handleApplePaySuccess = async (paymentMethod: any) => {
    setSubmitting(true);
    try {
      const finalState = {
        boxes: boxes,
        collectionAddress: collectionAddress,
        collectionDate: state.collectionDate,
        deliveryAddress: deliveryAddress,
        dontKnowAddress: deliveryAddress == null,
        deliveryDate: state.deliveryDate,
        dontKnowDate: state.deliveryDate == null,
        storageRequired: storageRequired,
        suitcases: suitcases,
        suitcaseData: suitcaseData,
        eecProducts: eecCheckoutProducts,
        items: beginCheckoutItems,
        orderId: orderId,
        discount: appliedDiscount,
        // Removals specific fields
        removalService: state.removalService,
        selectedTier: state.selectedTier,
        questions: state.questions,
        moveDate: state.moveDate,
        selectedPacking: state.selectedPacking,
        selectedStorage: state.selectedStorage,
        wantsPacking: state.wantsPacking,
        wantsStorage: state.wantsStorage,
      };

      let data: any;

      // Check if this is a removals order
      if (state.removalService && state.selectedTier && state.moveDate) {
        // Build removals order request
        const removalRequest: RemovalOrderRequest = {
          tierId: state.selectedTier.id,
          tierName: state.selectedTier.name,
          tierPrice: state.selectedTier.price,
          propertyQuestions: {
            floor: state.questions.floor,
            hasLift: state.questions.hasLift,
            hasFurniture: state.questions.hasFurniture,
            hasDisassembly: state.questions.hasDisassembly,
            hasParking: state.questions.hasParking,
          },
          collectionAddress: simplifyAddress(state.collectionAddress),
          collectionPostcode: state.collectionAddress.postCode || '',
          collectionCity: state.collectionAddress.city || '',
          collectionCountry: state.collectionAddress.country || 'United Kingdom',
          deliveryAddress: state.deliveryAddress ? simplifyAddress(state.deliveryAddress) : '',
          deliveryPostcode: state.deliveryAddress?.postCode || '',
          deliveryCity: state.deliveryAddress?.city || '',
          deliveryCountry: state.deliveryAddress?.country || 'United Kingdom',
          moveDate: state.moveDate.getTime(),
          paymentMethodId: paymentMethod.id,
          note: flow,
        };

        // Add packing materials if selected
        if (state.wantsPacking && state.selectedPacking) {
          removalRequest.packingMaterials = {
            id: state.selectedPacking.id,
            name: state.selectedPacking.name,
            price: state.selectedPacking.price,
            items: state.selectedPacking.items,
            rrp: state.selectedPacking.rrp,
          };
        }

        // Add storage if selected
        if (state.wantsStorage && state.selectedStorage) {
          removalRequest.storage = {
            id: state.selectedStorage.id,
            name: state.selectedStorage.name,
            size: state.selectedStorage.size,
            price: state.selectedStorage.price,
          };
        }

        // Add discount code if present
        if (appliedDiscount?.discountCode) {
          removalRequest.discountCode = appliedDiscount.discountCode;
        }

        const response = await axiosKK.post<any>('/removals/order/create', removalRequest);
        data = response.data;
      } else {
        // Original box/suitcase order logic
        const suitcaseRequest: MakeSuitcaseOrderRequest = {
          suitcases: suitcases ? suitcases : [],
          city: state.collectionAddress.city,
          collectionAddress: simplifyAddress(state.collectionAddress),
          collectionDate: state.collectionDate.getTime(),
          deliveryAddress:
            state.deliveryAddress && simplifyAddress(state.deliveryAddress),
          deliveryDate: state.deliveryDate?.getTime(),
          discountCode: appliedDiscount?.discountCode,
          note: flow,
          paymentMethodId: paymentMethod.id,
          country: state.collectionAddress.country,
          collectionCountry: state.collectionAddress.country,
          collectionPostcode: state.collectionAddress.postCode,
          deliveryCountry: state.deliveryAddress?.country,
          deliveryPostcode: state.deliveryAddress?.postCode,
        };

        const request: MakeOrderRequest = {
          boxes: state.boxes.reduce((acc, each) => {
            acc[each.id] = each.count;
            return acc;
          }, {} as BoxCount),
          city: state.collectionAddress.city,
          collectionAddress: simplifyAddress(state.collectionAddress),
          collectionDate: state.collectionDate.getTime(),
          deliveryAddress:
            state.deliveryAddress && simplifyAddress(state.deliveryAddress),
          deliveryDate: state.deliveryDate?.getTime(),
          roomPack: false,
          storageRequired: state.storageRequired,
          discountCode: appliedDiscount?.discountCode,
          note: flow,
          paymentMethodId: paymentMethod.id,
          collectionCountry: state.collectionAddress.country,
          collectionPostcode: state.collectionAddress.postCode,
          deliveryCountry: state.deliveryAddress?.country,
          deliveryPostcode: state.deliveryAddress?.postCode,
        };

        const response = await axiosKK.post<MakeOrderResponse>(
          suitcases ? '/order/create/suitcase' : '/order/create',
          suitcases ? suitcaseRequest : request,
        );
        data = response.data;
      }

      const order = data.order;
      setOrderId(order.id);

      // Handle payment status (same as existing logic)
      handlePaymentStatus(data, finalState, null as any, paymentMethod.id);
    } catch (error) {
      errorMessage(setError)(error as Error);
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="card-payment-section">
        <Formik
          initialValues={initialValues}
          validateOnMount={true}
          validate={({
            name,
            cardNumberValidation,
            cvcValidation,
            expiryDateValidation,
            agreeToTerms,
            agreeToPackingRules,
          }) => {
            const errors: PaymentFormErrors = {};

            setBillingAddress(collectionAddress);
            if (!billingEventSentToGTM) {
              setBillingEventSentToGTM(true);
              eventTrackingCallback(eecBillingObject);
              eventTrackingCallback(billingInfoObject);
            }

            if (!validateAddress(billingAddress)) {
              errors.address = 'Invalid address';
            }

            if (!name || name?.length < 5) {
              errors.name = 'too short of a name';
            }

            if (!cardNumberValidation) {
              errors.cardNumberValidation = 'Card number incomplete';
            }

            if (!cvcValidation) {
              errors.cardNumberValidation = 'CVC incomplete';
            }

            if (!expiryDateValidation) {
              errors.cardNumberValidation = 'Expiry date incomplete';
            }

            if (!agreeToTerms) {
              errors.agreeToTerms =
                'You must agree to the terms and conditions';
            }

            if (!agreeToPackingRules) {
              errors.agreeToPackingRules =
                'You must agree to the packing requirements';
            }

            if (Object.keys(errors).length === 0 && !paymentEventSentToGTM) {
              setPaymentEventSentToGTM(true);
              eventTrackingCallback(eecPaymentObject);
              eventTrackingCallback(paymentInfoObject);
            }

            return errors;
          }}
          onSubmit={async ({ name }) => {
            setSubmitting(true);

            if (!stripe || !elements) {
              throw new Error('Payment gateway not prepared');
            }

            const cardNumber = elements.getElement(CardNumberElement);
            const cvcNumber = elements.getElement(CardCvcElement);
            const expiryDate = elements.getElement(CardExpiryElement);

            if (!(cardNumber && cvcNumber && expiryDate)) {
              throw Error('Card elements not ready');
            }

            setCardNumber(cardNumber);

            const { paymentMethod, error } = await stripe.createPaymentMethod({
              type: 'card',
              card: cardNumber,
              billing_details: {
                name,
                address: {
                  line1: billingAddress.line1,
                  line2: billingAddress.line2,
                  city: billingAddress.city,
                  state: billingAddress.county,
                  postal_code: billingAddress.postCode,
                  country: mapCountryToCountryCode(billingAddress.country),
                },
              },
            });

            if (error) {
              errorMessage(setError)(error.toString());
              throw error;
            }

            if (!paymentMethod) {
              errorMessage(setError)('Unable to Retrieve Payment Method');
              throw new Error('Payment method not prepared');
            }

            setPaymentMethodId(paymentMethod.id);

            // Check if this is a removals order
            if (state.removalService && state.selectedTier && state.moveDate) {
              const finalState = {
                boxes: boxes,
                collectionAddress: collectionAddress,
                collectionDate: state.collectionDate,
                deliveryAddress: deliveryAddress,
                dontKnowAddress: deliveryAddress == null,
                deliveryDate: state.deliveryDate,
                dontKnowDate: state.deliveryDate == null,
                storageRequired: storageRequired,
                suitcases: suitcases,
                suitcaseData: suitcaseData,
                eecProducts: eecCheckoutProducts,
                items: beginCheckoutItems,
                orderId: orderId,
                discount: appliedDiscount,
                // Removals specific fields
                removalService: state.removalService,
                selectedTier: state.selectedTier,
                questions: state.questions,
                moveDate: state.moveDate,
                selectedPacking: state.selectedPacking,
                selectedStorage: state.selectedStorage,
                wantsPacking: state.wantsPacking,
                wantsStorage: state.wantsStorage,
              };
              // Build removals order request
              const removalRequest: RemovalOrderRequest = {
                tierId: state.selectedTier.id,
                tierName: state.selectedTier.name,
                tierPrice: state.selectedTier.price,
                propertyQuestions: {
                  floor: state.questions.floor,
                  hasLift: state.questions.hasLift,
                  hasFurniture: state.questions.hasFurniture,
                  hasDisassembly: state.questions.hasDisassembly,
                  hasParking: state.questions.hasParking,
                },
                collectionAddress: simplifyAddress(state.collectionAddress),
                collectionPostcode: state.collectionAddress.postCode || '',
                collectionCity: state.collectionAddress.city || '',
                collectionCountry: state.collectionAddress.country || 'United Kingdom',
                deliveryAddress: state.deliveryAddress ? simplifyAddress(state.deliveryAddress) : '',
                deliveryPostcode: state.deliveryAddress?.postCode || '',
                deliveryCity: state.deliveryAddress?.city || '',
                deliveryCountry: state.deliveryAddress?.country || 'United Kingdom',
                moveDate: state.moveDate.getTime(),
                paymentMethodId: paymentMethod.id,
                note: flow,
              };

              // Add packing materials if selected
              if (state.wantsPacking && state.selectedPacking) {
                removalRequest.packingMaterials = {
                  id: state.selectedPacking.id,
                  name: state.selectedPacking.name,
                  price: state.selectedPacking.price,
                  items: state.selectedPacking.items,
                  rrp: state.selectedPacking.rrp,
                };
              }

              // Add storage if selected
              if (state.wantsStorage && state.selectedStorage) {
                removalRequest.storage = {
                  id: state.selectedStorage.id,
                  name: state.selectedStorage.name,
                  size: state.selectedStorage.size,
                  price: state.selectedStorage.price,
                };
              }

              // Add discount code if present
              if (appliedDiscount?.discountCode) {
                removalRequest.discountCode = appliedDiscount.discountCode;
              }

              // Create removals order
              axiosKK
                .post<any>('/removals/order/create', removalRequest)
                .then(async ({ data }) => {
                  const order = data.order;
                  setOrderId(order.id);

                  setIntentClientSecret(data.paymentIntentSecret);

                  // Handle payment status
                  handlePaymentStatus(
                    data,
                    finalState,
                    cardNumber,
                    paymentMethod.id,
                  );

                  return order.id;
                })
                .catch((error) => {
                  errorMessage(setError)(error as Error);
                })
                .finally(() => setSubmitting(false));
            } else {
              // Original box/suitcase order logic
              const suitcaseRequest: MakeSuitcaseOrderRequest = {
                suitcases: suitcases ? suitcases : [],
                city: state.collectionAddress.city,
                collectionAddress: simplifyAddress(state.collectionAddress),
                collectionDate: state.collectionDate.getTime(),
                deliveryAddress:
                  state.deliveryAddress && simplifyAddress(state.deliveryAddress),
                deliveryDate: state.deliveryDate?.getTime(),
                discountCode: appliedDiscount?.discountCode,
                note: flow,
                paymentMethodId: paymentMethod.id,
                country: state.collectionAddress.country,
                collectionCountry: state.collectionAddress.country,
                collectionPostcode: state.collectionAddress.postCode,
                deliveryCountry: state.deliveryAddress?.country,
                deliveryPostcode: state.deliveryAddress?.postCode,
              };

              const request: MakeOrderRequest = {
                boxes: state.boxes.reduce((acc, each) => {
                  acc[each.id] = each.count;
                  return acc;
                }, {} as BoxCount),
                city: state.collectionAddress.city,
                collectionAddress: simplifyAddress(state.collectionAddress),
                collectionDate: state.collectionDate.getTime(),
                deliveryAddress:
                  state.deliveryAddress && simplifyAddress(state.deliveryAddress),
                deliveryDate: state.deliveryDate?.getTime(),
                roomPack: false,
                storageRequired: state.storageRequired,
                discountCode: appliedDiscount?.discountCode,
                note: flow,
                paymentMethodId: paymentMethod.id,
                collectionCountry: state.collectionAddress.country,
                collectionPostcode: state.collectionAddress.postCode,
                deliveryCountry: state.deliveryAddress?.country,
                deliveryPostcode: state.deliveryAddress?.postCode,
              };

              const finalState = {
                boxes: boxes,
                collectionAddress: collectionAddress,
                collectionDate: state.collectionDate,
                deliveryAddress: deliveryAddress,
                dontKnowAddress: deliveryAddress == null,
                deliveryDate: state.deliveryDate,
                dontKnowDate: state.deliveryDate == null,
                storageRequired: storageRequired,
                suitcases: suitcases,
                suitcaseData: suitcaseData,
                eecProducts: eecCheckoutProducts,
                items: beginCheckoutItems,
                orderId: orderId,
                discount: appliedDiscount,
                // Removals specific fields
                removalService: state.removalService,
                selectedTier: state.selectedTier,
                questions: state.questions,
                moveDate: state.moveDate,
                selectedPacking: state.selectedPacking,
                selectedStorage: state.selectedStorage,
                wantsPacking: state.wantsPacking,
                wantsStorage: state.wantsStorage,
              };

              axiosKK
                .post<MakeOrderResponse>(
                  suitcases ? '/order/create/suitcase' : '/order/create',
                  suitcases ? suitcaseRequest : request,
                )
                .then(async ({ data }) => {
                  const order = data.order;
                  setOrderId(order.id);

                  setIntentClientSecret(data.paymentIntentSecret);

                  handlePaymentStatus(
                    data,
                    finalState,
                    cardNumber,
                    paymentMethod.id,
                  );

                  return order.id;
                })
                .catch((error) => {
                  errorMessage(setError)(error as Error);
                })
                .finally(() => setSubmitting(false));
            }
          }}
        >
          {({ values, handleChange, handleSubmit, isValid, setFieldValue }) => (
            <form className="payment" onSubmit={handleSubmit}>
              <section className="content">
                <section className="all-details">
                  <div className="payment-container">
                    <div className="payment">
                      <div className="card-info">
                        <div className="title-bar">
                          <h5>Card Details</h5>
                        </div>
                        <div className="input-bar">
                          <div className="each">
                            <section>
                              <input
                                onChange={handleChange}
                                id="name"
                                value={values.name}
                                placeholder="Will Edwards"
                              />
                            </section>
                            <label className="payment-label">
                              Cardholder Name
                            </label>
                          </div>
                          <div className="each">
                            <section>
                              <CardNumberElement
                                options={cardNumberOptions}
                                onChange={(event) => {
                                  setFieldValue(
                                    'cardNumberValidation',
                                    event.complete,
                                  );
                                }}
                              />
                            </section>
                            <label className="payment-label">Card Number</label>
                          </div>
                          <div className="each-tgt">
                            <div className="each">
                              <section>
                                <CardCvcElement
                                  options={cardCvcOptions}
                                  onChange={(event) => {
                                    setFieldValue(
                                      'cvcValidation',
                                      event.complete,
                                    );
                                  }}
                                />
                              </section>
                              <label className="payment-label">CVC</label>
                            </div>
                            <div className="each">
                              <section>
                                <CardExpiryElement
                                  options={cardExpiryOptions}
                                  onChange={(event) => {
                                    setFieldValue(
                                      'expiryDateValidation',
                                      event.complete,
                                    );
                                  }}
                                />
                              </section>
                              <label className="payment-label">
                                Expiry Date
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="address">
                      <div className="title-bar">
                        <h5>Billing Address</h5>
                      </div>

                      <div className="billing-options">
                        <label className="billing-option">
                          <input
                            className="billing-option-checkbox"
                            type="checkbox"
                            checked={selectedBillingOption === 'collection'}
                            onChange={() => {
                              if (selectedBillingOption === 'collection') {
                                setSelectedBillingOption(null);
                                setShowBillingAddressLookup(true);
                              } else {
                                setSelectedBillingOption('collection');
                                setBillingAddress(collectionAddress);
                                setShowBillingAddressLookup(false);
                                if (!billingEventSentToGTM) {
                                  setBillingEventSentToGTM(true);
                                  eventTrackingCallback(eecBillingObject);
                                  eventTrackingCallback(billingInfoObject);
                                }
                              }
                            }}
                          />
                          Use collection address
                        </label>

                        <div className="billing-note">
                          <span>
                            Please use collection address for international
                            payment cards
                          </span>
                        </div>
                      </div>

                      {showBillingAddressLookup && (
                        <AddressLookup
                          address={billingAddress}
                          setAddress={setBillingAddress}
                          callback={eventTrackingCallback}
                          eecBillingInfo={eecBillingObject}
                          billingInfo={billingInfoObject}
                          billingEventSentToGTM={billingEventSentToGTM}
                          setBillingEventSentToGTM={setBillingEventSentToGTM}
                        />
                      )}
                    </div>
                  </div>
                  <ApplePayButton
                    paymentState={state}
                    appliedDiscount={appliedDiscount}
                    onPaymentSuccess={handleApplePaySuccess}
                  />
                </section>

                <section className="all-order">
                  {/* Pay Now Section */}
                  <div className="order-summary">
                    <div
                      className="order-header"
                      onClick={() => setShowPayNowDetails(!showPayNowDetails)}
                    >
                      <h3>Pay Now</h3>
                      <div className="order-dropdown">
                        <span>
                          {state.removalService && state.selectedTier
                            ? (() => {
                                // Calculate removals total
                                let total = state.selectedTier.price || 0;
                                if (state.selectedPacking) {
                                  total += state.selectedPacking.price;
                                }
                                if (state.questions?.hasDisassembly) {
                                  total += FURNITURE_DISASSEMBLY_FEE;
                                }
                                // Apply discount if present
                                if (appliedDiscount?.upFront) {
                                  total = total * (1 - appliedDiscount.discountPercentage / 100);
                                }
                                return `£${(total / 100).toFixed(2).replace('.00', '')}`;
                              })()
                            : suitcases
                            ? suitcaseOrderToMoney(
                                suitcases.length,
                                suitcaseData?.shippingCost,
                                appliedDiscount,
                              )
                            : storageRequired
                            ? payNow(
                                boxes,
                                appliedDiscount,
                                needsEmptyBoxDelivery,
                                collectionAddress,
                              )
                            : orderCostToMoney(
                                boxes,
                                needsEmptyBoxDelivery,
                                appliedDiscount,
                              )}
                        </span>
                        {showPayNowDetails ? (
                          <ChevronUpIcon
                            className="dropdown-icon"
                            style={{
                              width: '20px',
                              height: '20px',
                              color: '#424770',
                            }}
                          />
                        ) : (
                          <ChevronDownIcon
                            className="dropdown-icon"
                            style={{
                              width: '20px',
                              height: '20px',
                              color: '#424770',
                            }}
                          />
                        )}
                      </div>
                    </div>

                    {showPayNowDetails && (
                      <div className="order-details">
                        {state.removalService && state.selectedTier ? (
                          <>
                            <div className="order-line">
                              <h4>{state.selectedTier.name} Removal</h4>
                              <h3>£{(state.selectedTier.price / 100).toFixed(2).replace('.00', '')}</h3>
                            </div>
                            {state.selectedPacking && (
                              <div className="order-line">
                                <h4>{state.selectedPacking.name}</h4>
                                <h3>£{(state.selectedPacking.price / 100).toFixed(2).replace('.00', '')}</h3>
                              </div>
                            )}
                            {state.questions?.hasDisassembly && (
                              <div className="order-line">
                                <h4>Furniture Disassembly Service</h4>
                                <h3>£{(FURNITURE_DISASSEMBLY_FEE / 100).toFixed(2).replace('.00', '')}</h3>
                              </div>
                            )}
                            {appliedDiscount?.upFront && (
                              <div className="order-line discount">
                                <h4>Discount ({appliedDiscount.discountPercentage}%)</h4>
                                <h3 className="discount-amount">
                                  -£{(((state.selectedTier.price + (state.selectedPacking?.price || 0) + (state.questions?.hasDisassembly ? FURNITURE_DISASSEMBLY_FEE : 0)) * appliedDiscount.discountPercentage / 100) / 100).toFixed(2).replace('.00', '')}
                                </h3>
                              </div>
                            )}
                          </>
                        ) : (
                          boxes.map(
                            (box) =>
                              box.count > 0 && (
                                <div key={box.id} className="order-line">
                                  <h4>
                                    {box.name} &times; {box.count}
                                  </h4>
                                  <h3>{boxToMoney(box)}</h3>
                                </div>
                              ),
                          )
                        )}

                        {needsEmptyBoxDelivery && (
                          <div className="order-line">
                            <h4>Delivery of empty boxes</h4>
                            <h3>{toMoney(deliveryFee)}</h3>
                          </div>
                        )}

                        {storageRequired && (
                          <>
                            <div className="order-line">
                              <h4>Collection fee</h4>
                              <h3>{toMoney(COLLECTION_FEE)}</h3>
                            </div>

                            {collectionAddress.postCode &&
                              isOutOfArea(collectionAddress.postCode) && (
                                <div className="order-line">
                                  <h4>Collection surcharge</h4>
                                  <h3>{toMoney(COLLECTION_SURCHARGE)}</h3>
                                </div>
                              )}
                          </>
                        )}

                        {!storageRequired && !suitcases && (
                          <div className="order-line">
                            <h4>
                              Shipping Cost (Box &times; {totalBoxesCount})
                            </h4>
                            <h3>{toMoney(deliveryCost)}</h3>
                          </div>
                        )}

                        {appliedDiscount && appliedDiscount.upFront && (
                          <div className="order-line discount">
                            <h4>Discount applied</h4>
                            <h3>
                              -{appliedDiscount.discountPercentage}% (
                              {calculateDiscountAmount()})
                            </h3>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {(storageRequired || (state.removalService && state.selectedStorage)) && (
                    <div className="order-summary monthly">
                      <div
                        className="order-header"
                        onClick={() =>
                          setShowPayMonthlyDetails(!showPayMonthlyDetails)
                        }
                      >
                        <h3>
                          Pay Monthly<span className="asterisk">*</span>
                        </h3>
                        <div className="order-dropdown">
                          <span>
                            {state.removalService && state.selectedStorage
                              ? (() => {
                                  let monthlyTotal = state.selectedStorage.price;
                                  if (appliedDiscount?.recurring) {
                                    monthlyTotal = monthlyTotal * (1 - appliedDiscount.discountPercentage / 100);
                                  }
                                  return `£${(monthlyTotal / 100).toFixed(2).replace('.00', '')}`;
                                })()
                              : monthlyCostToMoney(boxes, appliedDiscount)}
                          </span>
                          {showPayMonthlyDetails ? (
                            <ChevronUpIcon
                              className="dropdown-icon"
                              style={{
                                width: '20px',
                                height: '20px',
                                color: '#424770',
                              }}
                            />
                          ) : (
                            <ChevronDownIcon
                              className="dropdown-icon"
                              style={{
                                width: '20px',
                                height: '20px',
                                color: '#424770',
                              }}
                            />
                          )}
                        </div>
                      </div>

                      {showPayMonthlyDetails && (
                        <div className="order-details">
                          {state.removalService && state.selectedStorage ? (
                            <>
                              <div className="order-line">
                                <h4>{state.selectedStorage.name} ({state.selectedStorage.size})</h4>
                                <h3>£{(state.selectedStorage.price / 100).toFixed(2).replace('.00', '')}</h3>
                              </div>
                              {appliedDiscount && appliedDiscount.recurring && (
                                <div className="order-line discount">
                                  <h4>Discount ({appliedDiscount.discountPercentage}%)</h4>
                                  <h3 className="discount-amount">
                                    -£{((state.selectedStorage.price * appliedDiscount.discountPercentage / 100) / 100).toFixed(2).replace('.00', '')}
                                  </h3>
                                </div>
                              )}
                            </>
                          ) : (
                            <>
                              {boxes.map(
                                (box) =>
                                  box.count > 0 && (
                                    <div key={box.id} className="order-line">
                                      <h4>
                                        {box.name} &times; {box.count}
                                      </h4>
                                      <h3>
                                        {toMoney(box.storageCost * box.count)}
                                      </h3>
                                    </div>
                                  ),
                              )}

                              {appliedDiscount && appliedDiscount.recurring && (
                                <div className="order-line discount">
                                  <h4>Discount applied</h4>
                                  <h3>
                                    -{appliedDiscount.discountPercentage}% (
                                    {calculateMonthlyDiscountAmount()})
                                  </h3>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {storageRequired && (
                    <div className="order-summary final-payment">
                      <div
                        className="order-header"
                        onClick={() =>
                          setShowFinalPaymentDetails(!showFinalPaymentDetails)
                        }
                      >
                        <h3>
                          Redelivery<span className="asterisk">**</span>
                        </h3>
                        <div className="order-dropdown">
                          <span>{toMoney(finalPaymentTotal)}</span>
                          {showFinalPaymentDetails ? (
                            <ChevronUpIcon
                              className="dropdown-icon"
                              style={{
                                width: '20px',
                                height: '20px',
                                color: '#424770',
                              }}
                            />
                          ) : (
                            <ChevronDownIcon
                              className="dropdown-icon"
                              style={{
                                width: '20px',
                                height: '20px',
                                color: '#424770',
                              }}
                            />
                          )}
                        </div>
                      </div>

                      {showFinalPaymentDetails && (
                        <div className="order-details">
                          {numberOfBoxesWithReturnFee > 0 && (
                            <div className="order-line">
                              <h4>
                                Redelivery of boxes &times;{' '}
                                {numberOfBoxesWithReturnFee}
                              </h4>
                              <h3>{toMoney(returnFeeTotal)}</h3>
                            </div>
                          )}

                          {differentAreaSurcharge > 0 && (
                            <div className="order-line">
                              <h4>
                                Different area surcharge (
                                {numberOfBoxesWithReturnFee} boxes)
                              </h4>
                              <h3>{toMoney(differentAreaSurcharge)}</h3>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {storageRequired && (
                    <div className="monthly-note-visible">
                      <p>
                        *Subject to change if we collect a different number of
                        boxes than booked.
                      </p>
                      <p>
                        **Redelivery due 5 days before your boxes are returned
                        to you.
                      </p>
                    </div>
                  )}
                  <DiscountComponent
                    successfulDiscount={(discount) =>
                      setAppliedDiscount(discount)
                    }
                  />
                </section>
              </section>

              <span className="terms-and-conditions">
                <input
                  name="dontKnowAddress"
                  onChange={() => {
                    setFieldValue('agreeToTerms', !values.agreeToTerms);
                  }}
                  value={values.agreeToTerms ? 'true' : 'false'}
                  type="checkbox"
                />

                <label htmlFor="dontKnowAddress">
                  I Agree to the
                  <a
                    href="https://www.kitkeeper.co.uk/terms-and-conditions/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Terms & Conditions
                  </a>
                </label>
              </span>

              <span className="terms-and-conditions">
                <input
                  name="agreeToPackingRules"
                  onChange={() => {
                    setFieldValue('agreeToPackingRules', !values.agreeToPackingRules);
                  }}
                  value={values.agreeToPackingRules ? 'true' : 'false'}
                  type="checkbox"
                />

                <label htmlFor="agreeToPackingRules">
                  I Agree to the
                  <button
                    type="button"
                    onClick={() => setShowPackingModal(true)}
                  >
                    Packing Requirements
                  </button>
                </label>
              </span>

              {submitting && (
                <h3 className="kkUltraViolet">
                  Payment is being processed, please do not refresh the page.
                </h3>
              )}

              {error && (
                <>
                  <h3 className="error">Error: </h3>
                  <h3 className="error">{error}</h3>
                </>
              )}

              {paymentError && (
                <>
                  <h3 className="error">Payment Error: </h3>
                  <h3 className="error">{paymentError}</h3>
                </>
              )}

              <SubmitButton
                className="payment-btn"
                disabled={!isValid}
                isSubmitting={submitting}
              >
                Confirm Payment
                <img
                  alt=""
                  src="/img/right-arrow-circular-button-outline.png"
                />
              </SubmitButton>
            </form>
          )}
        </Formik>
      </div>
      {pathname.includes('3ds-secure') && (
        <ThreeDSModal url={threeDSRedirectUrl} />
      )}

      {/* Packing Requirements Modal */}
      <PackingRequirementsModal
        isOpen={showPackingModal}
        onClose={() => setShowPackingModal(false)}
      />
    </>
  );
}

interface DiscountCodeProps {
  successfulDiscount: (discount: Discount) => void;
}

function DiscountComponent(props: DiscountCodeProps): JSX.Element {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [discountCode, setDiscountCode] = useState<string>('');

  return (
    <div className="discount-info">
      <Label htmlFor="discount" className="discount-label">
        Discount
      </Label>
      <div className="input-and-btn">
        <input
          id="discount"
          type="text"
          placeholder="Enter Discount Code"
          value={discountCode}
          onChange={(event) =>
            setDiscountCode(event.target.value.toUpperCase())
          }
        />
        <SubmitButton
          type="button"
          isSubmitting={submitting}
          onClick={(event) => {
            event.preventDefault();
            setSubmitting(true);

            axiosKK
              .get<DiscountResponse>(`/discountCode/${discountCode}`)
              .then(({ data }) => {
                props.successfulDiscount(data?.discount);
              })
              .catch((err) => {
                console.error(err);
              })
              .finally(() => {
                setSubmitting(false);
              });
          }}
        >
          Submit
        </SubmitButton>
      </div>
    </div>
  );
}

function PaymentWrapper(props: PaymentProps) {
  return (
    <Elements stripe={stripePromise}>
      <PaymentComponent {...props} />
    </Elements>
  );
}

export default withRouter(PaymentWrapper);
